import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DollarSign, TrendingUp, TrendingDown, Clock, Zap, FileText, CreditCard, Receipt, RefreshCw, CheckCircle, XCircle, Download, Plus } from 'lucide-react';
import { VerticalTabs } from '@/components/VerticalTabs';
import { toast } from 'sonner';

export default function AdminAccounting() {
  const { token } = useAuth();
  const [pnl, setPnl] = useState(null);
  const [aging, setAging] = useState(null);
  const [journal, setJournal] = useState([]);
  const [payments, setPayments] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [expFilter, setExpFilter] = useState('all');
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');

  const fetchData = () => {
    Promise.all([
      api.get('/accounting/pnl').catch(() => ({ data: null })),
      api.get('/accounting/ar-aging').catch(() => ({ data: null })),
      api.get('/accounting/journal').catch(() => ({ data: [] })),
      api.get('/accounting/payments').catch(() => ({ data: [] })),
      api.get('/accounting/recurring').catch(() => ({ data: [] })),
      api.get('/expenses').catch(() => ({ data: [] })),
      api.get('/settlements/list').catch(() => ({ data: [] })),
      api.get('/invoices').catch(() => ({ data: [] })),
    ]).then(([pnlR, agingR, journalR, payR, recR, expR, stlR, invR]) => {
      setPnl(pnlR.data); setAging(agingR.data); setJournal(journalR.data);
      setPayments(payR.data); setRecurring(recR.data); setExpenses(expR.data || []);
      setSettlements(stlR.data || []); setInvoices(invR.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleInit = async () => {
    try { const res = await api.post('/accounting/accounts/init'); toast.success(res.data.message); } catch { toast.error('Failed'); }
  };

  const handleAutoGenerate = async () => {
    try { const res = await api.post('/accounting/journal/auto-generate'); toast.success(res.data.message); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleExpenseAction = async (expenseId, action) => {
    try {
      await api.put(`/expenses/${expenseId}/${action}`);
      toast.success(`Expense ${action}d`);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleExportForTaxes = () => {
    const rows = [['Type', 'Date', 'Description', 'Amount', 'Category', 'Status']];
    journal.forEach(e => rows.push(['Journal', e.date, e.description, e.amount, `${e.debit_account} -> ${e.credit_account}`, e.reference_type || '']));
    payments.forEach(p => rows.push(['Payment', p.date, p.payment_type?.replace(/_/g, ' '), p.amount, p.payment_method || '', p.status]));
    expenses.forEach(e => rows.push(['Expense', e.date, e.description, e.amount, e.category, e.status]));
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `torq-accounting-export-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Export downloaded');
  };

  const tabsWithAdd = ['recurring', 'settlements', 'expenses', 'payments', 'receivables'];
  const tabs = [
    { id: 'overview', label: 'P&L Overview', icon: TrendingUp },
    { id: 'receivables', label: 'Receivables', icon: Clock },
    { id: 'payables', label: 'Payables', icon: TrendingDown },
    { id: 'journal', label: 'Journal', icon: FileText },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'expenses', label: 'Expenses', icon: Receipt, count: expenses.filter(e => e.status === 'pending').length },
    { id: 'settlements', label: 'Settlements', icon: DollarSign, count: settlements.filter(s => s.status === 'pending').length },
    { id: 'recurring', label: 'Recurring', icon: RefreshCw },
  ].map(t => tabsWithAdd.includes(t.id) ? { ...t, suffix: (
    <button onClick={e => { e.stopPropagation(); toast.success(`Create new ${t.label}`); }}
      className="ml-auto w-5 h-5 flex items-center justify-center rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors">
      <Plus className="w-3 h-3" />
    </button>
  )} : t);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Accounting</h1>
          <p className="text-muted-foreground text-sm">Financial overview and management</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground tracking-wider font-bold">DATE RANGE</span>
          <input type="date" value={dateRangeStart} onChange={e => setDateRangeStart(e.target.value)}
            className="bg-muted border border-input text-foreground text-xs rounded-md px-2 py-1 appearance-none cursor-pointer h-8" />
          <span className="text-muted-foreground text-xs">to</span>
          <input type="date" value={dateRangeEnd} onChange={e => setDateRangeEnd(e.target.value)}
            className="bg-muted border border-input text-foreground text-xs rounded-md px-2 py-1 appearance-none cursor-pointer h-8" />
          <Button className="bg-brand-accent text-brand-accent-foreground text-xs font-bold h-8" onClick={handleExportForTaxes}>
            <Download className="w-3 h-3 mr-1" /> Export
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {pnl && (
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-card border-border"><CardContent className="p-4">
            <TrendingUp className="w-4 h-4 text-emerald-400 mb-2" strokeWidth={1.5} />
            <p className="text-xl font-bold text-emerald-400">${pnl.total_revenue?.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Revenue</p>
          </CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4">
            <TrendingDown className="w-4 h-4 text-red-400 mb-2" strokeWidth={1.5} />
            <p className="text-xl font-bold text-red-400">${pnl.total_expenses?.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Expenses</p>
          </CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4">
            <DollarSign className="w-4 h-4 text-brand-accent mb-2" strokeWidth={1.5} />
            <p className={`text-xl font-bold ${pnl.net_income >= 0 ? 'text-brand-accent' : 'text-red-400'}`}>${pnl.net_income?.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Net Income</p>
          </CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4">
            <Clock className="w-4 h-4 text-amber-400 mb-2" strokeWidth={1.5} />
            <p className="text-xl font-bold text-amber-400">${aging?.total_outstanding?.toLocaleString() || 0}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Outstanding AR</p>
          </CardContent></Card>
        </div>
      )}

      {/* Vertical Tabs */}
      <VerticalTabs tabs={tabs} activeTab={tab} onTabChange={setTab}>
        {/* P&L */}
        {tab === 'overview' && pnl && (
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-400">Revenue</CardTitle></CardHeader>
              <CardContent>
                {(pnl.revenue || []).map(r => (
                  <div key={r.account_code} className="flex justify-between py-2 border-b border-border last:border-0">
                    <span className="text-xs text-muted-foreground">{r.name}</span><span className="text-xs text-emerald-400">${r.amount?.toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-3 mt-2 border-t border-input">
                  <span className="text-xs text-foreground font-bold">Total</span><span className="text-xs text-emerald-400 font-bold">${pnl.total_revenue?.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-red-400">Expenses</CardTitle></CardHeader>
              <CardContent>
                {(pnl.expenses || []).map(r => (
                  <div key={r.account_code} className="flex justify-between py-2 border-b border-border last:border-0">
                    <span className="text-xs text-muted-foreground">{r.name}</span><span className="text-xs text-red-400">${r.amount?.toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-3 mt-2 border-t border-input">
                  <span className="text-xs text-foreground font-bold">Total</span><span className="text-xs text-red-400 font-bold">${pnl.total_expenses?.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Receivables */}
        {tab === 'receivables' && aging && (
          <div className="grid grid-cols-2 gap-4">
            {[
              { key: 'current', label: 'Current (0-30)', color: 'emerald' },
              { key: '30_days', label: '31-60 Days', color: 'amber' },
              { key: '60_days', label: '61-90 Days', color: 'orange' },
              { key: '90_plus', label: '90+ Days', color: 'red' },
            ].map(b => (
              <Card key={b.key} className="bg-card border-border">
                <CardHeader className="pb-2"><CardTitle className={`text-sm text-${b.color}-400`}>{b.label}</CardTitle></CardHeader>
                <CardContent>
                  <p className={`text-xl font-bold text-${b.color}-400`}>${aging[b.key]?.total?.toLocaleString() || 0}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{aging[b.key]?.count || 0} invoices</p>
                  {(aging[b.key]?.invoices || []).slice(0, 3).map(inv => (
                    <div key={inv.invoice_id} className="flex justify-between py-1 text-xs mt-1 border-t border-border">
                      <span className="text-muted-foreground">{inv.invoice_number}</span><span className="text-muted-foreground">${inv.amount}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Payables */}
        {tab === 'payables' && (
          <div className="grid grid-cols-2 gap-4">
            {[
              { key: 'current', label: 'Current', color: 'emerald' },
              { key: '30_days', label: '31-60 Days', color: 'amber' },
              { key: '60_days', label: '61-90 Days', color: 'orange' },
              { key: '90_plus', label: '90+ Days', color: 'red' },
            ].map(b => (
              <Card key={b.key} className="bg-card border-border">
                <CardHeader className="pb-2"><CardTitle className={`text-sm text-${b.color}-400`}>{b.label}</CardTitle></CardHeader>
                <CardContent>
                  <p className={`text-xl font-bold text-${b.color}-400`}>$0</p>
                  <p className="text-[10px] text-muted-foreground mt-1">0 invoices</p>
                  <p className="text-xs text-muted-foreground mt-2">No payables data</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Journal */}
        {tab === 'journal' && (
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">DATE</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">DESCRIPTION</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">DEBIT</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">CREDIT</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">AMOUNT</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">REF</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {journal.map(e => (
                <TableRow key={e.entry_id} className="border-border">
                  <TableCell className="text-xs text-muted-foreground">{e.date}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.description}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{e.debit_account}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{e.credit_account}</TableCell>
                  <TableCell className="text-xs text-foreground font-mono">${e.amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-[10px] text-muted-foreground">{e.reference_type}</TableCell>
                </TableRow>
              ))}
              {journal.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No entries. Click "Auto-Generate".</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}

        {/* Payments */}
        {tab === 'payments' && (
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">DATE</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">TYPE</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">AMOUNT</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">METHOD</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">STATUS</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {payments.map(p => (
                <TableRow key={p.payment_id} className="border-border">
                  <TableCell className="text-xs text-muted-foreground">{p.date}</TableCell>
                  <TableCell className="text-xs text-muted-foreground capitalize">{p.payment_type?.replace(/_/g, ' ')}</TableCell>
                  <TableCell className="text-xs text-foreground font-mono">${p.amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-muted-foreground capitalize">{p.payment_method?.replace(/_/g, ' ') || '-'}</TableCell>
                  <TableCell><Badge className={p.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>{p.status}</Badge></TableCell>
                </TableRow>
              ))}
              {payments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No payments</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}

        {/* Expenses (moved from AdminExpenses) */}
        {tab === 'expenses' && (
          <div className="space-y-4">
            <div className="flex gap-2">
              {['all', 'pending', 'approved', 'rejected'].map(f => (
                <button key={f} onClick={() => setExpFilter(f)}
                  className={`text-[10px] tracking-wider font-bold px-3 py-1.5 rounded-lg transition-colors capitalize ${
                    expFilter === f ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'text-muted-foreground border border-border'
                  }`}>{f} ({f === 'all' ? expenses.length : expenses.filter(e => e.status === f).length})</button>
              ))}
            </div>
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">DATE</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">DRIVER</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">CATEGORY</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">DESCRIPTION</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">AMOUNT</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">STATUS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">ACTIONS</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(expFilter === 'all' ? expenses : expenses.filter(e => e.status === expFilter)).map(e => (
                  <TableRow key={e.expense_id} className="border-border">
                    <TableCell className="text-xs text-muted-foreground">{e.date}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.driver_id?.slice(-6)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground capitalize">{e.category}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{e.description}</TableCell>
                    <TableCell className="text-xs text-foreground font-mono">${e.amount}</TableCell>
                    <TableCell><Badge className={`text-[10px] ${e.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : e.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>{e.status}</Badge></TableCell>
                    <TableCell>
                      {e.status === 'pending' && (
                        <div className="flex gap-1">
                          <Button size="sm" className="h-6 text-[10px] bg-emerald-600" onClick={() => handleExpenseAction(e.expense_id, 'approve')}>
                            <CheckCircle className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-500/30 text-red-400" onClick={() => handleExpenseAction(e.expense_id, 'reject')}>
                            <XCircle className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Settlements (moved from AdminPayments) */}
        {tab === 'settlements' && (
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">ID</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">DRIVER</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">GROSS PAY</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">DEDUCTIONS</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">NET PAY</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">RPM</TableHead>
              <TableHead className="text-muted-foreground text-[10px] tracking-wide">STATUS</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {settlements.map(s => (
                <TableRow key={s.settlement_id} className="border-border">
                  <TableCell className="text-xs text-muted-foreground font-mono">{s.settlement_id?.slice(-8)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{s.driver_id?.slice(-6)}</TableCell>
                  <TableCell className="text-xs text-foreground font-mono">${s.gross_pay?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-red-400 font-mono">-${((s.fuel_advances || 0) + (s.insurance_fees || 0)).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-brand-accent font-bold font-mono">${s.net_pay?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">${s.rpm?.toFixed(2)}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${s.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>{s.status}</Badge></TableCell>
                </TableRow>
              ))}
              {settlements.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No settlements</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}

        {/* Recurring */}
        {tab === 'recurring' && (
          <div className="space-y-2">
            {recurring.length === 0 ? <p className="text-muted-foreground text-center py-8">No recurring charges</p> :
              recurring.map(r => (
                <div key={r.charge_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-foreground font-medium">{r.description}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{r.frequency} · {r.category || 'Other'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-foreground font-bold">${r.amount}</p>
                    <p className="text-[10px] text-muted-foreground">Next: {r.next_charge_date}</p>
                  </div>
                </div>
              ))}
          </div>
        )}
      </VerticalTabs>
    </div>
  );
}
